import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Heart, ArrowLeft, Loader2 } from 'lucide-react';

const DonatePage = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('25');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt < 1) { toast.error('Please enter an amount of at least $1'); return; }
    if (!email || !email.includes('@')) { toast.error('Please enter a valid email so we can send your receipt'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/donate`, {
        email: email.trim(),
        amount: amt,
        origin_url: window.location.origin,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not start donation'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="donate-page">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/individuals')} className="text-muted-foreground hover:text-foreground" data-testid="donate-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Donate to CircleUpSquad</h1>
        </div>
      </header>

      <main className="flex-1 max-w-lg mx-auto w-full px-5 py-10">
        <div className="relative w-16 h-16 flex items-center justify-center mx-auto mb-5">
          <div className="absolute inset-0 rounded-full border-2 border-cyan-300/40 animate-pulse" />
          <div className="absolute inset-2 rounded-full border-2 border-cyan-400/60" />
          <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
            <Heart className="w-4 h-4 text-white" fill="currentColor" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-center text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Help us keep this free for families
        </h2>
        <p className="text-muted-foreground text-center mt-2">
          CircleUpSquad is free for care circles up to 5 members. Your donation helps us reach more working adults navigating cancer and serious illness — no account required.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4 surface-card rounded-2xl p-6 border-2 border-cyan-100 dark:border-cyan-900/40">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Choose an amount</label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {[10, 25, 50, 100].map(v => (
                <button key={v} type="button"
                  onClick={() => setAmount(String(v))}
                  className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${amount === String(v) ? 'bg-cyan-600 text-white' : 'bg-muted text-foreground hover:bg-muted/80'}`}
                  data-testid={`public-donate-preset-${v}`}
                >
                  ${v}
                </button>
              ))}
            </div>
            <div className="flex items-center border border-input rounded-lg bg-background overflow-hidden mt-2">
              <span className="px-3 text-muted-foreground">$</span>
              <input type="number" min="1" step="1" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="Custom amount"
                className="flex-1 py-2 pr-3 bg-transparent text-sm text-foreground focus:outline-none"
                data-testid="public-donate-amount" />
            </div>
          </div>

          <div>
            <label htmlFor="donor-email" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your email (for receipt)</label>
            <Input id="donor-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com" className="mt-2" required data-testid="public-donate-email" />
          </div>

          <Button type="submit" className="btn-primary w-full py-6 text-base" disabled={loading} data-testid="public-donate-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Heart className="w-4 h-4 mr-2" fill="currentColor" />}
            Donate {amount ? `$${amount}` : ''}
          </Button>

          <p className="text-[11px] text-muted-foreground text-center">Secure checkout by Stripe. One-time donation, no recurring charges.</p>
        </form>
      </main>
    </div>
  );
};

export default DonatePage;
